export const PR_RECORD_TYPES = [
  "heaviest_weight",
  "best_1rm",
  "best_set_volume",
  "best_session_volume",
  "best_set_reps",
  "most_session_reps",
  "longest_distance",
  "longest_time",
] as const;

export type PrRecordType = (typeof PR_RECORD_TYPES)[number];

export type ScoredSet = {
  exercise_id: string;
  log_id: string;
  set_num: number;
  ex_order: number;
  sub_order: number;
  reps: number | null;
  weight: number | null;
  time_seconds: number | null;
  distance_meters: number | null;
  tracking_type: string | null;
  set_type: string | null;
};

export type PriorBests = Record<PrRecordType, number | null>;

export const emptyPriorBests = (): PriorBests =>
  Object.fromEntries(PR_RECORD_TYPES.map((type) => [type, null])) as PriorBests;

export function estimated1rm(
  weight: number | null | undefined,
  reps: number | null | undefined,
): number | null {
  if (weight == null || reps == null || weight <= 0 || reps < 1) {
    return null;
  }
  if (reps === 1) {
    return weight;
  }
  if (reps > 12) {
    return null;
  }
  return weight * (1 + reps / 30);
}

export function setVolume(
  weight: number | null | undefined,
  reps: number | null | undefined,
): number | null {
  if (weight == null || reps == null || weight <= 0 || reps < 1) {
    return null;
  }
  return weight * reps;
}

export function isWarmupSet(setType: string | null | undefined): boolean {
  return setType === "warmup";
}

const positiveNumber = (value: number | null | undefined): number | null =>
  value != null && Number.isFinite(value) && value > 0 ? value : null;

const compareSetOrder = (a: ScoredSet, b: ScoredSet): number =>
  a.ex_order - b.ex_order || a.sub_order - b.sub_order || a.set_num - b.set_num;

type TrackingGroup = "strength" | "bodyweight" | "timed" | "distance";

type SessionTotals = {
  volume: number;
  reps: number;
};

type PrTypeSpec = {
  label: string;
  tracking: readonly TrackingGroup[];
  /** Cumulative session records can be stamped on save without a live toast. */
  liveToast: boolean;
} & (
  | { scope: "set"; ofSet: (set: ScoredSet) => number | null }
  | { scope: "session"; ofSession: (totals: SessionTotals) => number | null }
);

/**
 * Single source of truth for PR types. Scoring, live toasts, and record
 * snapshots all read from this table — add a type here instead of another switch.
 */
const PR_SPECS: Record<PrRecordType, PrTypeSpec> = {
  heaviest_weight: {
    label: "Heaviest weight",
    tracking: ["strength"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => positiveNumber(set.weight),
  },
  best_1rm: {
    label: "Best 1RM",
    tracking: ["strength"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => estimated1rm(set.weight, set.reps),
  },
  best_set_volume: {
    label: "Best set volume",
    tracking: ["strength"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => setVolume(set.weight, set.reps),
  },
  best_session_volume: {
    label: "Best session volume",
    tracking: ["strength"],
    liveToast: false,
    scope: "session",
    ofSession: (totals) => (totals.volume > 0 ? totals.volume : null),
  },
  best_set_reps: {
    label: "Best set",
    tracking: ["bodyweight"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => positiveNumber(set.reps),
  },
  most_session_reps: {
    label: "Most session reps",
    tracking: ["bodyweight"],
    liveToast: true,
    scope: "session",
    ofSession: (totals) => (totals.reps > 0 ? totals.reps : null),
  },
  longest_distance: {
    label: "Longest distance",
    tracking: ["distance"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => positiveNumber(set.distance_meters),
  },
  longest_time: {
    label: "Longest time",
    tracking: ["timed", "distance"],
    liveToast: true,
    scope: "set",
    ofSet: (set) => positiveNumber(set.time_seconds),
  },
};

export const PR_TYPE_LABELS = Object.fromEntries(
  PR_RECORD_TYPES.map((type) => [type, PR_SPECS[type].label]),
) as Record<PrRecordType, string>;

const trackingGroup = (
  trackingType: string | null | undefined,
): TrackingGroup => {
  const resolved = trackingType ?? "strength";
  if (resolved === "bodyweight" || resolved === "timed") {
    return resolved;
  }
  if (resolved.startsWith("distance")) {
    return "distance";
  }
  return "strength";
};

export function recordTypesForTracking(
  trackingType: string | null | undefined,
): PrRecordType[] {
  const group = trackingGroup(trackingType);
  return PR_RECORD_TYPES.filter((type) =>
    PR_SPECS[type].tracking.includes(group),
  );
}

export function isSessionScopedPr(type: PrRecordType): boolean {
  return PR_SPECS[type].scope === "session";
}

export function beatsPrior(
  value: number | null | undefined,
  prior: number | null | undefined,
): boolean {
  if (value == null || !Number.isFinite(value)) {
    return false;
  }
  if (prior == null || !Number.isFinite(prior)) {
    return false;
  }
  return value > prior;
}

const setKey = (set: ScoredSet) =>
  `${set.exercise_id}:${set.ex_order}:${set.sub_order}:${set.set_num}`;

export type SessionMetrics = {
  values: PriorBests;
  holders: Partial<Record<PrRecordType, ScoredSet>>;
};

export function sessionMetrics(sets: ScoredSet[]): SessionMetrics {
  const working = [...sets.filter((set) => !isWarmupSet(set.set_type))].sort(
    compareSetOrder,
  );
  const values = emptyPriorBests();
  const holders: Partial<Record<PrRecordType, ScoredSet>> = {};
  const totals: SessionTotals = { volume: 0, reps: 0 };

  for (const set of working) {
    totals.volume += setVolume(set.weight, set.reps) ?? 0;
    totals.reps += positiveNumber(set.reps) ?? 0;
    for (const type of PR_RECORD_TYPES) {
      const spec = PR_SPECS[type];
      if (spec.scope !== "set") {
        continue;
      }
      const value = spec.ofSet(set);
      if (value == null) {
        continue;
      }
      const current = values[type];
      if (current == null || value > current) {
        values[type] = value;
        holders[type] = set;
      }
    }
  }

  const lastSet = working[working.length - 1];
  if (lastSet) {
    for (const type of PR_RECORD_TYPES) {
      const spec = PR_SPECS[type];
      if (spec.scope !== "session") {
        continue;
      }
      const value = spec.ofSession(totals);
      if (value == null) {
        continue;
      }
      values[type] = value;
      holders[type] = lastSet;
    }
  }

  return { values, holders };
}

export function scoreSessionPrs(
  sets: ScoredSet[],
  priorByExercise: Map<string, PriorBests>,
): Map<string, PrRecordType[]> {
  const result = new Map<string, PrRecordType[]>();
  const byExercise = new Map<string, ScoredSet[]>();

  for (const set of sets) {
    result.set(setKey(set), []);
    if (isWarmupSet(set.set_type)) {
      continue;
    }
    const list = byExercise.get(set.exercise_id) ?? [];
    list.push(set);
    byExercise.set(set.exercise_id, list);
  }

  for (const [exerciseId, exerciseSets] of byExercise) {
    const prior = priorByExercise.get(exerciseId) ?? emptyPriorBests();
    const allowed = new Set(
      recordTypesForTracking(exerciseSets[0]?.tracking_type),
    );
    const { values, holders } = sessionMetrics(exerciseSets);

    for (const type of allowed) {
      const holder = holders[type];
      if (!holder || !beatsPrior(values[type], prior[type])) {
        continue;
      }
      const key = setKey(holder);
      const current = result.get(key) ?? [];
      current.push(type);
      result.set(key, current);
    }
  }

  return result;
}

export function parsePrTypes(value: unknown): PrRecordType[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const allowed = new Set<string>(PR_RECORD_TYPES);
  return value.filter(
    (item): item is PrRecordType =>
      typeof item === "string" && allowed.has(item),
  );
}

export function countPrEvents(
  sets: { pr_types?: unknown; is_pr?: boolean }[],
): number {
  return sets.reduce((sum, set) => {
    const types = parsePrTypes(set.pr_types);
    if (types.length > 0) {
      return sum + types.length;
    }
    return set.is_pr ? sum + 1 : sum;
  }, 0);
}

export function priorBestsFromRecords(
  records: { recordType: PrRecordType; value: number }[],
): PriorBests {
  const prior = emptyPriorBests();
  for (const record of records) {
    prior[record.recordType] = record.value;
  }
  return prior;
}

export function hasAnyPrior(prior: PriorBests): boolean {
  return (Object.values(prior) as (number | null)[]).some(
    (value) => value != null && Number.isFinite(value),
  );
}

export function priorBestsFromLoggedSets(
  sets: Array<{
    reps?: number | null;
    weight?: number | null;
    time_seconds?: number | null;
    distance_meters?: number | null;
    set_type?: string | null;
  }>,
  trackingType?: string | null,
): PriorBests {
  const scored: ScoredSet[] = sets.map((set, index) => ({
    exercise_id: "_",
    log_id: "_",
    set_num: index,
    ex_order: 0,
    sub_order: 0,
    reps: set.reps ?? null,
    weight: set.weight ?? null,
    time_seconds: set.time_seconds ?? null,
    distance_meters: set.distance_meters ?? null,
    tracking_type: trackingType ?? "strength",
    set_type: set.set_type ?? null,
  }));
  return sessionMetrics(scored).values;
}

export function coalescePriorBests(
  fromRecords: PriorBests,
  fromLoggedSets: PriorBests,
): PriorBests {
  return hasAnyPrior(fromRecords) ? fromRecords : fromLoggedSets;
}

const valueForPrType = (
  type: PrRecordType,
  ticked: ScoredSet,
  session: SessionMetrics,
): number | null => {
  const spec = PR_SPECS[type];
  return spec.scope === "set" ? spec.ofSet(ticked) : session.values[type];
};

export function toastTypesForTick(
  ticked: ScoredSet,
  completedIncludingTicked: ScoredSet[],
  prior: PriorBests,
): PrRecordType[] {
  if (isWarmupSet(ticked.set_type)) {
    return [];
  }

  const session = sessionMetrics(completedIncludingTicked);
  return recordTypesForTracking(ticked.tracking_type).filter((type) => {
    if (!PR_SPECS[type].liveToast) {
      return false;
    }
    return beatsPrior(valueForPrType(type, ticked, session), prior[type]);
  });
}

export function applyToastTypes(
  prior: PriorBests,
  types: PrRecordType[],
  ticked: ScoredSet,
  completedIncludingTicked: ScoredSet[],
): PriorBests {
  const session = sessionMetrics(completedIncludingTicked);
  const next = { ...prior };
  for (const type of types) {
    const value = valueForPrType(type, ticked, session);
    if (value != null) {
      next[type] = value;
    }
  }
  return next;
}

export function replayCompletedSets(
  baseline: PriorBests,
  completedInOrder: ScoredSet[],
): PriorBests {
  return stampCompletedSetPrs(baseline, completedInOrder).live;
}

export function stampCompletedSetPrs(
  baseline: PriorBests,
  completedInOrder: ScoredSet[],
): { live: PriorBests; typesBySetNum: Map<number, PrRecordType[]> } {
  let live = { ...baseline };
  const soFar: ScoredSet[] = [];
  const typesBySetNum = new Map<number, PrRecordType[]>();
  for (const scored of completedInOrder) {
    soFar.push(scored);
    const types = toastTypesForTick(scored, soFar, live);
    typesBySetNum.set(scored.set_num, types);
    if (types.length === 0) {
      continue;
    }
    live = applyToastTypes(live, types, scored, soFar);
  }
  return { live, typesBySetNum };
}

export function keepLatestPrTypes(
  typesBySetNum: Map<number, PrRecordType[]>,
): Map<number, PrRecordType[]> {
  const ownerByType = new Map<PrRecordType, number>();
  for (const [setNum, types] of typesBySetNum) {
    for (const type of types) {
      const current = ownerByType.get(type);
      if (current == null || setNum > current) {
        ownerByType.set(type, setNum);
      }
    }
  }
  const next = new Map<number, PrRecordType[]>();
  for (const [setNum, types] of typesBySetNum) {
    next.set(
      setNum,
      types.filter((type) => ownerByType.get(type) === setNum),
    );
  }
  return next;
}

export function prValueForType(
  type: PrRecordType,
  ticked: ScoredSet,
  completedIncludingTicked: ScoredSet[],
): number | null {
  return valueForPrType(
    type,
    ticked,
    sessionMetrics(completedIncludingTicked),
  );
}

export type HistoricSet = ScoredSet & { start_time: string };

export type AllTimeRecord = {
  exerciseId: string;
  type: PrRecordType;
  value: number;
  holder: HistoricSet;
  achievedAt: string;
};

export type AllTimeSetRecord = {
  exerciseId: string;
  holder: HistoricSet;
  achievedAt: string;
};

const isBetterRecord = (
  value: number,
  candidate: HistoricSet,
  bestValue: number,
  best: HistoricSet,
) => {
  if (value !== bestValue) {
    return value > bestValue;
  }
  if (candidate.start_time !== best.start_time) {
    return candidate.start_time < best.start_time;
  }
  return compareSetOrder(candidate, best) < 0;
};

export function allTimeRecords(sets: HistoricSet[]): {
  records: AllTimeRecord[];
  setRecords: AllTimeSetRecord[];
} {
  const byExercise = new Map<string, HistoricSet[]>();
  for (const set of sets) {
    if (isWarmupSet(set.set_type)) {
      continue;
    }
    const list = byExercise.get(set.exercise_id) ?? [];
    list.push(set);
    byExercise.set(set.exercise_id, list);
  }

  const records: AllTimeRecord[] = [];
  const setRecords: AllTimeSetRecord[] = [];

  for (const [exerciseId, exerciseSets] of byExercise) {
    for (const type of PR_RECORD_TYPES) {
      const spec = PR_SPECS[type];
      let best: HistoricSet | undefined;
      let bestValue = Number.NEGATIVE_INFINITY;

      if (spec.scope === "set") {
        for (const set of exerciseSets) {
          if (!recordTypesForTracking(set.tracking_type).includes(type)) {
            continue;
          }
          const value = spec.ofSet(set);
          if (value == null) {
            continue;
          }
          if (!best || isBetterRecord(value, set, bestValue, best)) {
            best = set;
            bestValue = value;
          }
        }
      } else {
        const sessions = new Map<string, HistoricSet[]>();
        for (const set of exerciseSets) {
          if (!recordTypesForTracking(set.tracking_type).includes(type)) {
            continue;
          }
          const key = `${set.log_id}:${set.tracking_type ?? "strength"}`;
          const list = sessions.get(key) ?? [];
          list.push(set);
          sessions.set(key, list);
        }
        for (const group of sessions.values()) {
          const metrics = sessionMetrics(group);
          const value = metrics.values[type];
          const holder = metrics.holders[type] as HistoricSet | undefined;
          if (value == null || !holder) {
            continue;
          }
          if (!best || isBetterRecord(value, holder, bestValue, best)) {
            best = holder;
            bestValue = value;
          }
        }
      }

      if (best) {
        records.push({
          exerciseId,
          type,
          value: bestValue,
          holder: best,
          achievedAt: best.start_time,
        });
      }
    }

    const bestByReps = new Map<number, HistoricSet>();
    for (const set of exerciseSets) {
      if (trackingGroup(set.tracking_type) !== "strength") {
        continue;
      }
      const reps = set.reps;
      const weight = set.weight;
      if (reps == null || reps < 1 || weight == null || weight <= 0) {
        continue;
      }
      const existing = bestByReps.get(reps);
      if (
        !existing ||
        isBetterRecord(weight, set, existing.weight ?? 0, existing)
      ) {
        bestByReps.set(reps, set);
      }
    }
    for (const holder of bestByReps.values()) {
      setRecords.push({
        exerciseId,
        holder,
        achievedAt: holder.start_time,
      });
    }
  }

  return { records, setRecords };
}

export function priorBestsByExercise(
  sets: HistoricSet[],
): Map<string, PriorBests> {
  const map = new Map<string, PriorBests>();
  for (const record of allTimeRecords(sets).records) {
    const prior = map.get(record.exerciseId) ?? emptyPriorBests();
    prior[record.type] = record.value;
    map.set(record.exerciseId, prior);
  }
  return map;
}
